import posthog from "posthog-js";
import { initPostHog } from "@/lib/posthog";
import type { Preferences } from "./types";

// Consent UI, capture, geo-targeting, and Google Consent Mode updates are ALL
// owned by CookieYes's native banner (SDK loaded in src/app/layout.tsx; per-
// region banner display is configured in the CookieYes dashboard, and
// non-targeted regions get an implied-grant cookie the reader below treats as
// granted). This module only READS CookieYes's cookie and applies the one
// side effect CookieYes can't: driving the npm-bundled PostHog SDK.

// CookieYes's own consent cookie — the single source of truth, written only
// by the SDK. Trust rule: only copies carrying a `consentid` entry count
// (CookieYes always writes one; the lookalike cookie our pre-CookieYes code
// wrote under this same name never did, so legacy copies are ignored).
//
// READ THE PER-CATEGORY FLAGS, NEVER `consent`/`action`. Those two are
// law-dependent and one of them is inverted, so gating on them is wrong.
// Verified live against the SDK, 2026-08-05 (proxidize runs a GDPR opt-in
// banner in EU/UK + listed countries, and a US-State-Laws opt-out config
// everywhere else):
//
//   law   state              consent / action   categories
//   GDPR  pre-decision       no      / ''      all no      -> deny
//   GDPR  accept all         yes     / yes     all yes     -> grant
//   GDPR  reject all         no      / yes     all no      -> deny
//   CCPA  pre-action         no      / ''      all YES     -> GRANT (opt-out model)
//   CCPA  opted out          YES     / yes     all no      -> deny
//   CCPA  opted back in      no      / yes     all yes     -> grant
//
// Note the trap: under CCPA `consent:yes` means the visitor OPTED OUT, the
// opposite of its GDPR meaning. The category flags are the only fields whose
// meaning is stable across both laws, so they are what we read.
//
// DASHBOARD INVARIANT this depends on: the GDPR-side per-category "default
// state" must stay DENIED (CookieYes dashboard → Cookie Manager → category →
// the GDPR column; fixed 2026-08-04). If anyone re-enables "load cookie prior
// to consent" there, the GDPR pre-decision cookie starts reading all-yes and
// this reader would grant before consent in the EU. The SDK's own
// getCkyConsent().categories is law-aware and would be safer, but it is not
// available at first paint, which is why the cookie stays the primary read.
//
// Must match the inline gtag bootstrap in src/app/layout.tsx.
export const CONSENT_COOKIE = "cookieyes-consent";

export const DEFAULT_PREFS: Preferences = {
  functional: false,
  analytics: false,
  performance: false,
  advertisement: false,
};

function parseConsentValue(raw: string): { prefs: Preferences; fromCookieYes: boolean } {
  const value = decodeURIComponent(raw.slice(CONSENT_COOKIE.length + 1));
  const entries: Record<string, string> = {};
  value.split(",").forEach((kv) => {
    const idx = kv.indexOf(":");
    if (idx !== -1) entries[kv.slice(0, idx)] = kv.slice(idx + 1);
  });
  return {
    prefs: {
      functional: entries.functional === "yes",
      analytics: entries.analytics === "yes",
      performance: entries.performance === "yes",
      advertisement: entries.advertisement === "yes",
    },
    fromCookieYes: !!entries.consentid,
  };
}

/**
 * Reads CookieYes's recorded consent state.
 *
 * `consented` means "CookieYes has written a record we can read" — NOT "the
 * visitor clicked something". Under the opt-out (US State Laws) config the
 * grants are live before any interaction, so gating trackers on an explicit
 * action would deny analytics to almost everyone in those regions. Callers
 * should therefore drive behaviour from `prefs`, using `consented` only to
 * tell "no readable record yet" (SDK still booting, blocked, or localhost)
 * apart from "recorded, and here are the grants".
 */
export function parseConsent(): { consented: boolean; prefs: Preferences } {
  if (typeof document === "undefined") return { consented: false, prefs: DEFAULT_PREFS };

  const trusted = document.cookie
    .split(";")
    .map((c) => c.trim())
    .filter((c) => c.startsWith(`${CONSENT_COOKIE}=`))
    .map(parseConsentValue)
    .filter((c) => c.fromCookieYes);

  if (trusted.length === 0) return { consented: false, prefs: DEFAULT_PREFS };

  // Duplicate copies at different scopes can coexist; grant a category only
  // if every copy grants it, so the most restrictive record always wins.
  const prefs = trusted.reduce<Preferences>(
    (acc, c) => ({
      functional: acc.functional && c.prefs.functional,
      analytics: acc.analytics && c.prefs.analytics,
      performance: acc.performance && c.prefs.performance,
      advertisement: acc.advertisement && c.prefs.advertisement,
    }),
    { functional: true, analytics: true, performance: true, advertisement: true }
  );

  return { consented: true, prefs };
}

// Drives the npm-bundled PostHog SDK from the consent state. CookieYes's
// script blocking can't reach code compiled into our bundle, so this is the
// one tracker we must start/stop ourselves. Called by ConsentSync whenever
// CookieYes reports a consent change (and once at boot for the implied-grant
// first visit).
export function applyPostHogConsent(analyticsGranted: boolean) {
  if (typeof window === "undefined") return;
  if (analyticsGranted) {
    initPostHog();
    posthog.set_config({ persistence: "localStorage+cookie" });
    posthog.opt_in_capturing({ captureEventName: null });
  } else {
    if (posthog.__loaded) {
      posthog.opt_out_capturing();
      posthog.set_config({ persistence: "memory" });
    }
    // Cleanup must NOT hide behind the __loaded guard: a visitor arriving
    // with an already-rejected cookie (e.g. rejected on billing, returning
    // here) never boots PostHog this page view, but stale ph_* identifiers
    // from an earlier accept may still sit in the cookie jar/localStorage
    // and would otherwise survive the recorded rejection indefinitely.
    clearPosthogCookies();
  }
}

// PostHog doesn't delete its identity cookie/storage on opt-out; remove them
// so a rejection leaves no tracking identifiers behind.
function clearPosthogCookies() {
  const rootDomain = window.location.hostname.split(".").slice(-2).join(".");
  document.cookie.split(";").forEach((c) => {
    const name = c.trim().split("=")[0];
    if (!name.startsWith("ph_")) return;
    document.cookie = `${name}=;path=/;max-age=0`;
    document.cookie = `${name}=;path=/;domain=.${rootDomain};max-age=0`;
  });
  Object.keys(window.localStorage)
    .filter((k) => k.startsWith("ph_"))
    .forEach((k) => window.localStorage.removeItem(k));
}
